import React, { useEffect, useState } from "react";
import { Button, Space, Modal, notification, FormInstance, Dropdown, Menu } from "antd";
import { ActionConfig, ActionType, ColumnConfig, FormColumnConfig, MasterConfigs } from "../../interfaces/entity";
import ReusableTable from "../TableComponent";
import ControlesComponent from "./controles";
import FormSetup from "./FormSetup";
import { useNavigate, useNavigation, useLocation } from "react-router-dom";
import { DeleteOutlined, EditOutlined, ExclamationCircleOutlined, EyeOutlined, MoreOutlined } from '@ant-design/icons'
import { useSelector } from "react-redux";
import { deleteRow } from "../../actions/apiCall";
import { useDispatch } from "react-redux";
const { confirm } = Modal;

interface Props {
    config: MasterConfigs
    form?: (e: FormInstance) => void;
    isModal?: boolean;
    recordId?: string | null;
}

const MasterComponent: React.FC<Props> = ({
    config,
    form,
    isModal = false,
    recordId = null
}) => {
    const [visible, setVisible] = useState(false);
    const [filterVisible, setFilterVisible] = useState(false);

    const [isView, setIsView] = useState(false);
    const [id, setId] = useState(null);
    const { user }: any = useSelector((app: any) => app?.app);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        if (recordId) {
            setId(recordId);
            setIsView(false);
            setVisible(true);
        }
    }, [recordId]);

    useEffect(() => {
        if (!visible) {
            setId(null)
            setIsView(false)
        }
    }, [visible])

    const onActions = (actionType: ActionType) => {
        if (config?.setInitialForm) {
            config.setInitialForm()
        }
        switch (actionType) {
            case 'Add':
                return setVisible(true);
            case "Back":
                return navigate(-1);
            case "filter":
                return setFilterVisible(!filterVisible)
        }
    }

    const getAccess = () => {
        let currentPage = [];
        if (user?.designation == 'Admin') {
            currentPage = [{ create: true, view: true, edit: true }];
        } else {
            const currentPath = location.pathname;
            // Normalize paths: ensure leading slash, remove trailing slash
            const normalizePath = (path: string) => {
                if (!path) return '';
                // Ensure path starts with / and remove trailing slash
                const normalized = path.startsWith('/') ? path : `/${path}`;
                return normalized.replace(/\/$/, '') || '/';
            };

            currentPage = user?.access?.filter(
                (ele) => {
                    if (!ele?.routing?.routeUrl) return false;

                    const routeUrl = normalizePath(ele.routing.routeUrl);
                    const path = normalizePath(currentPath);

                    // Exact match
                    if (routeUrl === path) return true;

                    // Prefix match: current path starts with routeUrl followed by / or end of string
                    // e.g., routeUrl="/leads" matches path="/leads/list" or path="/leads/contacts"
                    if (path.startsWith(routeUrl + '/') || path.startsWith(routeUrl + '?')) {
                        return true;
                    }

                    return false;
                }
            );
        }
        return currentPage && currentPage.length > 0 ? currentPage[0] : {};
    };

    const getColumns = () => {
        return config.getColumns ? config.getColumns() : [];
    }
    const OverrideConfig: MasterConfigs = {
        ...config,
        getColumns: () => {
            const access = getAccess();
            return [
                ...getColumns(),
                config?.isActionTab && {
                    title: 'Actions',
                    dataIndex: '',
                    key: '',
                    width: 100,
                    fixed: 'right',
                    align: 'center',
                    resizable: false,
                    render: (text) => {
                        const menuItems = [];

                        // Default actions
                        if (access && access.view) {
                            menuItems.push(
                                <Menu.Item
                                    key="view"
                                    icon={<EyeOutlined />}
                                    onClick={() => onUpdate(text)}
                                >
                                    View
                                </Menu.Item>
                            );
                        }

                        if (access && access.edit) {
                            menuItems.push(
                                <Menu.Item
                                    key="edit"
                                    icon={<EditOutlined />}
                                    onClick={() => onUpdate(text, false)}
                                >
                                    Edit
                                </Menu.Item>
                            );
                        }

                        if (access && access.edit) {
                            menuItems.push(
                                <Menu.Item
                                    key="delete"
                                    icon={<DeleteOutlined />}
                                    danger
                                    onClick={() => showDeleteConfirm(text._id)}
                                >
                                    Delete
                                </Menu.Item>
                            );
                        }

                        // Add custom actions if provided
                        if (config?.customActionsItems) {
                            const customItems = config.customActionsItems(text);
                            customItems.forEach(item => {
                                menuItems.push(
                                    <Menu.Item
                                        key={item.key}
                                        icon={item.icon}
                                        danger={item.danger}
                                        onClick={item.onClick}
                                    >
                                        {item.label}
                                    </Menu.Item>
                                );
                            });
                        }

                        const menu = <Menu>{menuItems}</Menu>;

                        return menuItems.length > 0 ? (
                            <Dropdown overlay={menu} trigger={['click']} placement="bottomRight">
                                <Button type="text" icon={<MoreOutlined />} />
                            </Dropdown>
                        ) : null;
                    }
                }
            ].filter(Boolean) as FormColumnConfig[]
        }
    }

    const onUpdate = (data, isView = true) => {
        setId(data._id)
        setIsView(isView)
        setVisible(true)
    }

    const showNotification = (type, msg, disc) => {
        notification[type]({
            message: msg,
            description: disc,
        });
    };

    const onDelete = async (id) => {
        // Check if custom delete handler is provided
        if (config?.onDelete) {
            const response = await config.onDelete(id);
            if (response?.code === 200) {
                showNotification(
                    'success',
                    'Success',
                    'Record Successfully deleted'
                );
                dispatch({
                    type: 'REFRESH_TABLE',
                    refreshTable: true
                });
            } else {
                showNotification('error', 'Error', response?.message || 'Something Went wrong');
            }
            return;
        }

        // Default delete behavior
        const response = await dispatch(
            deleteRow(`${config.api()}/${id}`)
        );
        if (response.code !== 0) {
            showNotification(
                'success',
                'Success',
                'Record Successfully deleted'
            );
            dispatch({
                type: 'REFRESH_TABLE',
                refreshTable: true
            })
        } else {
            showNotification('error', 'Error', 'Something Went wrong');
        }
    };

    const showDeleteConfirm = (id) => {
        confirm({
            title: 'Are you sure want delete this?',
            icon: <ExclamationCircleOutlined />,
            content: '',
            okText: 'Yes',
            okType: 'danger',
            cancelText: 'No',
            onOk() {
                onDelete(id);
            },
            onCancel() {
            },
        });
    };

    const onClose = () => {
        if (config.onClose) {
            config.onClose()
        }
        setId(null)
        setFilterVisible(false)
        setIsView(false)
        setVisible(false)
    }

    const access = getAccess();

    return <>
        <FormSetup id={id} isView={isView} config={config} open={visible} onClose={() => onClose()} form={(e) => form ? form(e) : (e) => { }} isModal={isModal} />
        <Space direction="vertical" style={{ width: '100%' }}>
            <ControlesComponent filterVisible={filterVisible} config={config} setAction={(action) => onActions(action)} access={access} />
            {config.renderSummary && config.renderSummary()}
            <ReusableTable config={OverrideConfig} />
        </Space>
    </>
}

export default MasterComponent;

